/**
 * OneSignal push notification service (Capacitor native + browser web).
 * Routes to platform-specific implementations while sharing session dedupe logic.
 */

import { Capacitor } from '@capacitor/core';
import { initializeChatNotificationClickListeners } from '@/services/oneSignal/chatNotificationHandler';
import {
  initializeOneSignalNative,
  isOneSignalNativeAvailable,
  logOutOneSignalNative,
  requestNotificationPermissionNative,
  syncOneSignalUserNative,
} from '@/services/oneSignal/native';
import {
  getOneSignalAppId,
  normalizePhoneForOneSignal,
  resolveArtistId,
  type SyncOneSignalUserParams,
} from '@/services/oneSignal/shared';
import {
  initializeOneSignalWeb,
  isOneSignalWebAvailable,
  logOutOneSignalWeb,
  requestNotificationPermissionWeb,
  syncOneSignalUserWeb,
} from '@/services/oneSignal/web';

export type { SyncOneSignalUserParams };
export { getOneSignalAppId, normalizePhoneForOneSignal, resolveArtistId };

/** True when OneSignal can run on the current platform and app id is configured. */
export function isOneSignalAvailable(): boolean {
  if (!getOneSignalAppId()) return false;
  return Capacitor.isNativePlatform() ? isOneSignalNativeAvailable() : isOneSignalWebAvailable();
}

/** Initialize OneSignal once at app startup (native or web). */
export async function initializeOneSignal(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await initializeOneSignalNative();
  } else {
    await initializeOneSignalWeb();
  }
  await initializeChatNotificationClickListeners();
}

/** Request notification permission on first launch only. */
export async function requestNotificationPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    return requestNotificationPermissionNative();
  }
  return requestNotificationPermissionWeb();
}

/** Link the current client push subscription to the logged-in artist. */
export async function syncOneSignalUser(params: SyncOneSignalUserParams): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return syncOneSignalUserNative(params);
  }
  return syncOneSignalUserWeb(params);
}

/** Unlink the current artist from this client's push subscription. */
export async function logOutOneSignal(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return logOutOneSignalNative();
  }
  return logOutOneSignalWeb();
}
